import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  Calendar, 
  Clock, 
  ArrowRight, 
  Tag,
  Phone,
  ChevronRight,
  Search,
  Loader2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { SEOHead, breadcrumbSchema } from "@/components/SEOHead";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const NBR_LOGO = "https://nationalbusinessregister.co.uk/wp-content/uploads/2024/11/logo.png";

// Default blog images
const defaultImages = {
  "Case Studies": "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=800&h=400&fit=crop",
  "Guides": "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&h=400&fit=crop",
  "Education": "https://images.unsplash.com/photo-1456324504439-367cee3b3c32?w=800&h=400&fit=crop",
  "News": "https://images.unsplash.com/photo-1504711434969-e33886168f5c?w=800&h=400&fit=crop",
  "default": "https://images.unsplash.com/photo-1556761175-4b46a572b786?w=800&h=400&fit=crop"
};

const getImageUrl = (post) => {
  return post.image_url || defaultImages[post.category] || defaultImages.default;
};

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-GB', { 
    day: 'numeric', 
    month: 'long', 
    year: 'numeric' 
  });
};

export const BlogPage = () => {
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPosts();
  }, [selectedCategory, searchQuery]);

  const fetchPosts = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (selectedCategory !== "All") params.append("category", selectedCategory);
      if (searchQuery) params.append("search", searchQuery);
      
      const response = await fetch(`${API}/blog/posts?${params}`);
      if (!response.ok) throw new Error("Failed to fetch posts");
      
      const data = await response.json();
      setPosts(data.posts || []);
      setCategories(data.categories || []);
    } catch (error) {
      console.error("Error fetching posts:", error);
    } finally {
      setLoading(false);
    }
  };

  const featuredPost = posts.find(post => post.featured);

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Blog - Trade Mark Insights & Case Studies"
        description="Expert articles on trade mark monitoring, brand protection, and intellectual property from National Business Register. Case studies, guides, and industry insights."
        path="/blog"
        schema={breadcrumbSchema([
          { name: "Home", url: "/landing" },
          { name: "Blog", url: "/blog" }
        ])}
      />
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/landing" className="flex items-center gap-3">
              <img src={NBR_LOGO} alt="National Business Register" className="h-8 sm:h-10 w-auto" />
              <div className="hidden sm:block">
                <span className="font-heading font-black text-lg text-slate-900">NBR</span>
                <span className="text-xs text-teal-600 tracking-widest uppercase ml-1">Trade Mark Monitoring</span>
              </div>
            </Link>
            
            <div className="hidden md:flex items-center gap-6">
              <Link to="/landing#methodology" className="text-sm text-slate-600 hover:text-teal-600 transition-colors">Features</Link>
              <Link to="/landing#pricing" className="text-sm text-slate-600 hover:text-teal-600 transition-colors">Pricing</Link>
              <Link to="/landing#how-it-works" className="text-sm text-slate-600 hover:text-teal-600 transition-colors">How It Works</Link>
              <Link to="/blog" className="text-sm text-teal-600 font-medium">Blog</Link>
              <a href="tel:08000699090" className="text-sm text-slate-600 hover:text-teal-600 flex items-center gap-1 transition-colors">
                <Phone className="w-4 h-4" />
                0800 069 9090
              </a>
            </div>
            
            <div className="flex items-center gap-2 sm:gap-3">
              <Link to="/register-trademarks">
                <Button className="bg-teal-600 hover:bg-teal-700 text-white text-sm px-3 sm:px-4">
                  Get Started
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-24 sm:pt-32 pb-12 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-2 text-sm text-slate-500 mb-4">
              <Link to="/landing" className="hover:text-teal-600">Home</Link>
              <ChevronRight className="w-4 h-4" />
              <span className="text-slate-900">Blog</span>
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-heading font-black text-slate-900 mb-4">
              Trade Mark Insights & News
            </h1>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Expert guidance on protecting your brand, trade mark case studies, and the latest in intellectual property law.
            </p>
          </div>

          {/* Search Bar */}
          <div className="max-w-xl mx-auto mb-8">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="text"
                placeholder="Search articles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-3 rounded-full border border-slate-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all"
              />
            </div>
          </div>

          {/* Category Filter */}
          <div className="flex flex-wrap justify-center gap-2 mb-12">
            {categories.map((category) => (
              <button
                key={category.name}
                onClick={() => setSelectedCategory(category.name)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  selectedCategory === category.name
                    ? "bg-teal-600 text-white"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                {category.name} ({category.count})
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
        </div>
      )}

      {/* Featured Post */}
      {!loading && featuredPost && selectedCategory === "All" && searchQuery === "" && (
        <section className="px-4 sm:px-6 lg:px-8 pb-12">
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-slate-900 rounded-2xl overflow-hidden"
            >
              <div className="grid md:grid-cols-2">
                <div className="aspect-video md:aspect-auto">
                  <img 
                    src={getImageUrl(featuredPost)} 
                    alt={featuredPost.title}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-6 sm:p-8 lg:p-12 flex flex-col justify-center">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="bg-teal-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                      FEATURED
                    </span>
                    <span className="text-slate-400 text-sm">{featuredPost.category}</span>
                  </div>
                  <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-white mb-4">
                    {featuredPost.title}
                  </h2>
                  <p className="text-slate-300 mb-6 line-clamp-3">
                    {featuredPost.excerpt}
                  </p>
                  <div className="flex items-center gap-4 text-sm text-slate-400 mb-6">
                    <div className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {formatDate(featuredPost.date)}
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {featuredPost.reading_time || featuredPost.readingTime} min read
                    </div>
                  </div>
                  <Link to={`/blog/${featuredPost.slug}`}>
                    <Button className="bg-teal-600 hover:bg-teal-700 text-white w-fit">
                      Read Article
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </div>
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* Blog Posts Grid */}
      {!loading && (
      <section className="px-4 sm:px-6 lg:px-8 pb-20">
        <div className="max-w-7xl mx-auto">
          {posts.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-slate-500 text-lg">No articles found matching your criteria.</p>
              <button
                onClick={() => { setSelectedCategory("All"); setSearchQuery(""); }}
                className="mt-4 text-teal-600 hover:text-teal-700 font-medium"
              >
                Clear filters
              </button>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
              {posts
                .filter(post => !(post.featured && selectedCategory === "All" && searchQuery === ""))
                .map((post, index) => (
                <motion.article
                  key={post.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white rounded-xl border border-slate-200 overflow-hidden hover:shadow-lg hover:border-teal-200 transition-all group"
                >
                  <Link to={`/blog/${post.slug}`}>
                    <div className="aspect-video overflow-hidden">
                      <img 
                        src={getImageUrl(post)} 
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <div className="p-5 sm:p-6">
                      <div className="flex items-center gap-2 mb-3">
                        <span className="text-xs font-medium text-teal-600 bg-teal-50 px-2 py-1 rounded">
                          {post.category}
                        </span>
                        <span className="text-xs text-slate-400">
                          {post.reading_time || post.readingTime} min read
                        </span>
                      </div>
                      <h3 className="font-bold text-lg text-slate-900 mb-2 group-hover:text-teal-600 transition-colors line-clamp-2">
                        {post.title}
                      </h3>
                      <p className="text-slate-600 text-sm mb-4 line-clamp-2">
                        {post.excerpt}
                      </p>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1 text-xs text-slate-400">
                          <Calendar className="w-3 h-3" />
                          {formatDate(post.date)}
                        </div>
                        <span className="text-teal-600 text-sm font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
                          Read more <ArrowRight className="w-4 h-4" />
                        </span>
                      </div>
                    </div>
                  </Link>
                </motion.article>
              ))}
            </div>
          )}
        </div>
      </section>
      )}

      {/* CTA Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-teal-600">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl font-heading font-black text-white mb-4">
            Protect Your Trade Mark Today
          </h2>
          <p className="text-teal-100 mb-8 max-w-2xl mx-auto">
            Don't wait for infringement to happen. Start monitoring your brand with NBR Trade Mark Monitoring 
            and stay ahead of potential conflicts.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register-trademarks">
              <Button className="bg-white text-teal-700 hover:bg-slate-100 px-8 py-6">
                Get Started - £20/month
              </Button>
            </Link>
            <a href="tel:08000699090">
              <Button variant="outline" className="border-white text-white hover:bg-white/10 px-8 py-6">
                <Phone className="w-4 h-4 mr-2" />
                Call 0800 069 9090
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <img src={NBR_LOGO} alt="National Business Register" className="h-10 w-auto brightness-0 invert" />
              <div>
                <span className="font-heading font-black text-lg text-white">NBR</span>
                <span className="text-xs text-teal-400 tracking-widest uppercase ml-1">Trade Mark Monitoring</span>
              </div>
            </div>
            <p className="text-slate-400 text-sm text-center">
              © {new Date().getFullYear()} National Business Register Group Ltd. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default BlogPage;
